import { Drawer, Pagination, Table } from '@/components/commons';
import { DeleteContent } from '@/components/commons/DeleteContent';
import { ENDPOINT } from '@/services/endpoint';
import { IResponse, IResponseError } from '@/types/common';
import {
  CALENDAR_POST_STATUS_VALUE,
  convertRequest,
  handleErrorCommon,
  notificationMessage,
  notificationModal,
  NOTIFY_STATUS,
} from '@/utils';
import { ERROR, SUCCESS, SUCCESS_MESSAGE } from '@/utils/constants/messages';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Image, TableColumnsType } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { CalendarPostForm } from '../Drawers/CalendarPostForm';
import { formSchemaFn } from '../Drawers/CalendarPostForm/formSchemaFn';
import { FORM_NAME } from '../Drawers/CalendarPostForm/constants';
import {
  ICreateCalendarPostRequest,
  IUpdateCalendarPostRequest,
} from '@/types/request';
import { ImageService } from '@/services/image';
import { IUploadImageRequest } from '@/types/request/upload';
import {
  IGetDetailCalendarPostResponse,
} from '@/types/response';
import { CalendarPostService } from '@/services';

interface IProps {
  title?: string;
}

interface IParams {
  limit: number;
  page: number;
}

export interface ContentImage {
  url?: string;
  file?: File;
  previewUrl?: string;
}

const defaultImages = [null];
const defaultIcons = [null];


interface IParams {
  title: string;
  limit: number;
  page: number;
  isFukumaru: boolean;
  sortBy: string;
  filterType?: string | null;
}

export const CalendarPostList: React.FC<IProps> = ({ title }) => {
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [detail, setDetail] = useState<IGetDetailCalendarPostResponse>();
  const [isNotPermissionEdit, setIsNotPermissionEdit] =
    useState<boolean>(false);
  const [isNotFoundDetail, setIsNotFoundDetail] = useState<boolean>(false);
  const [params, setParams] = useState<IParams>({
    title: '',
    limit: 10,
    page: 1,
    isFukumaru: false,
    sortBy: 'createdAt',
    filterType: 'CALENDAR_POST',
  });

  const { data, refetch, isFetching } = useQuery(
    [ENDPOINT.CALENDAR_POST.GET_LIST, params],
    () => CalendarPostService.getList(convertRequest(params)),
  );

  const methods = useForm<any>({
    mode: 'onChange',
    resolver: yupResolver(formSchemaFn(!!currentId, isNotPermissionEdit)),
    reValidateMode: 'onChange',
  });

  const { handleSubmit, reset, watch, setValue, setError } = methods;

  const handleCancel = () => {
    setOpenDrawer(false);
    setCurrentId(null);
    setDetail(undefined);
    reset({ images: defaultImages });
  };

  const handleCreate = async (values: ICreateCalendarPostRequest) => {
    const { images, icons, ...rest } = values;
    const payload: ICreateCalendarPostRequest = {
      ...rest,
      images: [],
      icons: []
    };
    try {
      const imageUploadPromises = images?.map(async (image: any) => {
        if (image?.file) {
          const response = await ImageService.uploadFile(image.file);
          return response?.data?.url;
        }
        return image?.url;
      }) || [];

      const iconUploadPromises = icons?.map(async (icon: any) => {
        if (icon?.file) {
          const response = await ImageService.uploadFile(icon.file);
          return response?.data?.url;
        }
        return icon?.url;
      }) || [];

      const [uploadedImages, uploadedIcons] = await Promise.all([
        Promise.all(imageUploadPromises),
        Promise.all(iconUploadPromises),
      ]);

      payload.images = uploadedImages;
      payload.icons = uploadedIcons;
      await createCalendarPost(payload);
    } catch (error) {
      console.log({ error });
    }
  };

  const { mutate: createCalendarPost, isLoading: isLoadingCreate } = useMutation(
    (body: ICreateCalendarPostRequest) => CalendarPostService.create(body),
    {
      onSuccess: () => {
        notificationMessage({
          message:
            SUCCESS_MESSAGE[
            SUCCESS.CREATE_CALENDAR_POST as keyof typeof SUCCESS_MESSAGE
            ],
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );

  const { mutateAsync: uploadFile, isLoading: isLoadingUpload } = useMutation(
    (file: File) => ImageService.uploadFile(file),
    {
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );

  const { isFetching: isFetchingDetail } = useQuery(
    [ENDPOINT.CALENDAR_POST.GET_DETAIL.replace(':id', String(currentId))],
    () => CalendarPostService.getDetail(currentId!),
    {
      enabled: !!currentId,
      onSuccess: (response: IResponse<IGetDetailCalendarPostResponse>) => {
        const data = response?.data;
        setDetail(data);
        const payload: ICreateCalendarPostRequest = {
          headerTitle: data?.headerTitle,
          titleContent: data?.titleContent,
          title: data?.title,
          content: data?.content,
          publicAt: moment(data?.publicAt),
          images: [
            {
              url: data?.imageCover?.url,
              previewUrl: data?.imageCover?.url,
            },
          ],
          icons: [
            {
              url: data?.icon?.url,
              previewUrl: data?.icon?.url,
            },
          ],
          message: data?.message,
          talk: data?.talk,
          timeLine: data?.timeLine,
          status: data?.status === CALENDAR_POST_STATUS_VALUE.DRAFT ? 0 : 1,
        };
        reset({
          formName: FORM_NAME.UPDATE_CONTENT,
          isDisabled: data?.status === CALENDAR_POST_STATUS_VALUE.PUBLIC,
          ...payload,
        });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
        setIsNotFoundDetail(true);
      },
    },
  );

  const handleUpdate = async (values: IUpdateCalendarPostRequest) => {
    delete values.formName;
    delete values.isDisabled;
    const { images, icons, ...rest } = values;
    const payload: IUpdateCalendarPostRequest = {
      ...rest,
      icons: [],
      images: [],
    };
    try {
      const imageUploadPromises = images?.map(async (image: any) => {
        if (image?.file) {
          const response = await ImageService.uploadFile(image.file);
          return response?.data?.url;
        }
        return image?.url;
      }) || [];

      const iconUploadPromises = icons?.map(async (icon: any) => {
        if (icon?.file) {
          const response = await ImageService.uploadFile(icon.file);
          return response?.data?.url;
        }
        return icon?.url;
      }) || [];

      const [uploadedImages, uploadedIcons] = await Promise.all([
        Promise.all(imageUploadPromises),
        Promise.all(iconUploadPromises),
      ]);

      payload.images = uploadedImages;
      payload.icons = uploadedIcons;

      await updateCalendarPost({ body: payload, id: Number(currentId) });
    } catch (error) {
      console.log({ error });
    }
  };

  const handleDelete = (data: IGetDetailCalendarPostResponse) => {
    notificationModal({
      type: NOTIFY_STATUS.CONFIRM,
      title: '削除しますか？',
      content: <DeleteContent name='コンテンツ名' content={data?.headerTitle} />,
      maskClosable: false,
      onOk: () => deleteCalendarPost(currentId!),
      okText: '削除する',
      cancelText: 'キャンセル',
    });
  };

  const { mutate: updateCalendarPost, isLoading: isLoadingUpdate } = useMutation(
    (payload: { body: IUpdateCalendarPostRequest; id: number }) =>
      CalendarPostService.update(payload.body, payload.id),
    {
      onSuccess: () => {
        notificationMessage({
          message:
            SUCCESS_MESSAGE[
            SUCCESS.UPDATE_CALENDAR_POST as keyof typeof SUCCESS_MESSAGE
            ],
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);

        if (error.error === ERROR.CALENDAR_POST_NOT_FOUND) {
          setOpenDrawer(false);
          refetch();
        }
      },
    },
  );

  const onChangePagination = (page: number, pageSize: number) => {
    setParams({ ...params, page, limit: pageSize });
  };

  const { mutate: deleteCalendarPost, isLoading: isLoadingDelete } = useMutation(
    (id: number) => CalendarPostService.delete(id),
    {
      onSuccess: () => {
        notificationMessage({
          message:
            SUCCESS_MESSAGE[
            SUCCESS.DELETE_CALENDAR_POST as keyof typeof SUCCESS_MESSAGE
            ],
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);

        if (error.error === ERROR.CALENDAR_POST_RELOAD_DATA) {
          setOpenDrawer(false);
          refetch();
        }
      },
    },
  );

  const columns: TableColumnsType = [
    {
      title: 'コンテンツ名',
      dataIndex: 'headerTitle',
    },
    {
      title: 'コンテンツ画像',
      dataIndex: 'imageCover',
      align: 'center',
      render: (imageCover) =>
        imageCover?.url && (
          <Image preview={false} width={112} src={imageCover?.url} />
        ),
    },
    {
      title: '掲載日時',
      render: (value, record) => {
        return `${moment(record.publicAt).format('YYYY年MM月DD日 HH:mm')}`;
      },
    },
    {
      title: 'ステータス',
      width: '200px',
      render: (value, record) => {
        switch (record.status) {
          case CALENDAR_POST_STATUS_VALUE.DRAFT:
            return '下書き'
          case CALENDAR_POST_STATUS_VALUE.PUBLIC:
            return '配信中'
          default:
            return '配信予約済み';
        }
      },
    },
  ];

  const isDisabled = watch('isDisabled');

  useEffect(() => {
    setIsNotPermissionEdit(isDisabled);

    if (isNotFoundDetail) {
      setOpenDrawer(false);
      refetch();
    }
  }, [isDisabled, isNotFoundDetail]);

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px] h-full'>
        <header className='text-right'>
          <Button
            type='primary'
            onClick={() => {
              setOpenDrawer(true),
                reset({
                  images: defaultImages,
                  icons: defaultIcons,
                }),
                setValue('status', 0),
                setIsNotFoundDetail(false);
            }}
          >
            カレンダー投稿新規作成
          </Button>
        </header>

        <div className='flex justify-between flex-col'>
          <Table
            loading={isFetching || isLoadingUpload}
            scroll={{ x: 800 }}
            bordered
            rowKey='id'
            columns={columns}
            dataSource={data?.data.data}
            pagination={false}
            onRow={(record) => ({
              onClick: () => {
                setOpenDrawer(true);
                setCurrentId(record.id);
                setIsNotFoundDetail(false);
              },
            })}
            locale={{
              emptyText: 'カレンダー投稿はまだありません',
            }}
          />

          {data?.data?.pagination?.total! > 10 && (
            <Pagination
              className='pagination-table'
              total={data?.data.pagination.total}
              pageSize={params.limit}
              current={params.page}
              showSizeChanger={false}
              onChange={onChangePagination}
            />
          )}
        </div>
      </div>

      <Drawer
        title={!currentId ? 'カレンダー投稿新規作成' : 'カレンダー投稿詳細'}
        onOk={handleSubmit(currentId ? handleUpdate : handleCreate)}
        onCancel={handleCancel}
        onDelete={() => handleDelete(detail!)}
        maskClosable={false}
        open={openDrawer}
        okText={!currentId ? '保存 ' : '更新 '}
        loading={
          isLoadingUpdate ||
          isLoadingDelete ||
          isFetchingDetail ||
          isLoadingCreate ||
          isLoadingUpload
        }
        isDeleted={!!currentId}
        disabled={isDisabled}
        allowDelete={!isDisabled}
      >
        <FormProvider {...methods}>
          <CalendarPostForm
            isLoading={
              isFetchingDetail ||
              isLoadingUpdate ||
              isLoadingCreate ||
              isLoadingDelete
            }
            openDrawer={openDrawer}
          />
        </FormProvider>
      </Drawer>
    </div>
  );
};
